/**
 * Shape of a GetNotifications row plus the unwrapping the endpoint needs.
 * The gateway returns the array at one of several depths depending on the
 * wrapper, so every caller resolves it through `extractNotificationRows`.
 */
import type { NotificationItem } from '@wlb/shared/types/notification';

/**
 * A single GetNotifications row. The endpoint carries no `type` field — the
 * kind is derived from `notificationTypeName` by `resolveNotificationKind`.
 */
export interface NotificationRow {
  notificationId?: number;
  id?: string | number;
  type?: string;
  title?: string;
  notificationTypeName?: string;
  message?: string;
  createdAt?: string;
  time?: string;
  section?: string;
  isRead?: boolean;
  status?: string;
  actorProfilePicture?: string;
  actorUserId?: number;
  actorUserGuid?: string;
  senderId?: number;
  postId?: number;
  postCommentId?: number;
  groupId?: number;
  conversationId?: number;
  messageId?: number;
  user?: { name?: string; avatar?: string | { uri: string } | null };
}

/** First match wins, so the shallowest envelope is listed first. */
const ROW_PATHS: readonly (readonly string[])[] = [
  ['notifications'],
  ['data', 'notifications'],
  ['data', 'data'],
  ['data', 'data', 'list'],
  ['data', 'data', 'items'],
];

const readPath = (source: unknown, path: readonly string[]): unknown =>
  path.reduce<unknown>(
    (value, key) =>
      value && typeof value === 'object'
        ? (value as Record<string, unknown>)[key]
        : undefined,
    source,
  );

export const extractNotificationRows = (response: unknown): NotificationRow[] => {
  for (const path of ROW_PATHS) {
    const value = readPath(response, path);
    if (Array.isArray(value)) return value as NotificationRow[];
  }
  return [];
};

/**
 * Returns the real API id for row actions. Locally generated display ids must
 * never be sent to the delete endpoint.
 */
export const getNotificationApiId = (
  notification: Pick<NotificationItem, 'id' | 'notificationId'>,
): number | null => {
  const candidate = notification.notificationId
    ?? (/^\d+$/.test(notification.id) ? Number(notification.id) : null);

  return typeof candidate === 'number'
    && Number.isSafeInteger(candidate)
    && candidate > 0
    ? candidate
    : null;
};

/** GetNotifications is unpaginated on web — one page covers the bell list. */
export const NOTIFICATIONS_PAGE_SIZE = 100;
